import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import { BrandMark, Button, Input, Label, toast } from '@eon/ui';
import { hooks } from '@eon/api-client';
import { motion } from 'framer-motion';
import { Activity, Lock, Mail, Shield, Zap } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { LOGIN_ROLES } from '../auth/permissions';

interface LoginForm {
  email: string;
  password: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = hooks.useLogin({
    requireRoles: LOGIN_ROLES,
    requireRolesError: t('auth.notAuthorised'),
  });
  const { register, handleSubmit, formState } = useForm<LoginForm>({
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
      void navigate({ to: '/' });
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <div className="relative grid min-h-dvh overflow-hidden bg-[hsl(224,38%,4%)] lg:grid-cols-2">
      {/* ── Left panel ── */}
      <div className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#030712] via-[hsl(224,38%,6%)] to-[hsl(220,38%,3%)]" />

        {/* Dot-grid texture */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              'radial-gradient(circle, hsl(224,28%,18%) 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        {/* Animated orb — cyan */}
        <motion.div
          className="pointer-events-none absolute -left-20 top-1/4 h-[480px] w-[480px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, hsl(188,95%,48%,0.22) 0%, transparent 70%)',
          }}
          animate={{ scale: [1, 1.08, 1], x: [0, 20, 0] }}
          transition={{ repeat: Infinity, duration: 9, ease: 'easeInOut' }}
        />

        {/* Animated orb — energy green */}
        <motion.div
          className="pointer-events-none absolute bottom-1/4 right-0 h-[360px] w-[360px] rounded-full"
          style={{
            background:
              'radial-gradient(circle, hsl(142,71%,45%,0.15) 0%, transparent 70%)',
          }}
          animate={{ scale: [1, 1.05, 1], y: [0, -24, 0] }}
          transition={{ repeat: Infinity, duration: 11, ease: 'easeInOut', delay: 2 }}
        />

        {/* Content */}
        <div className="relative z-10 flex items-center gap-3">
          <BrandMark size="sm" />
          <span className="font-display text-lg font-semibold tracking-tight text-white">
            EON CSMS
          </span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative z-10 space-y-6"
        >
          <div className="space-y-3">
            <h2 className="font-display text-4xl font-bold leading-tight text-white">
              {t('app.tagline')}
            </h2>
            <p className="max-w-md text-sm text-white/60">{t('auth.subtitle')}</p>
          </div>

          {/* Feature bullets */}
          <div className="space-y-3">
            {[
              { icon: Activity, key: 'realtime' as const },
              { icon: Zap, key: 'ocpp' as const },
              { icon: Shield, key: 'analytics' as const },
            ].map(({ icon: Icon, key }) => (
              <div key={key} className="flex items-center gap-3">
                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white/10 ring-1 ring-white/10">
                  <Icon className="h-4 w-4 text-[hsl(188,95%,60%)]" />
                </div>
                <span className="text-sm text-white/70">{t(`auth.features.${key}`)}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <p className="relative z-10 text-xs text-white/30">
          © {new Date().getFullYear()} Eonus Energy
        </p>
      </div>

      {/* ── Right panel / Form ── */}
      <div className="flex items-center justify-center bg-[hsl(224,38%,4%)] p-6 lg:bg-[hsl(224,35%,5%)]">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md"
        >
          {/* Glassmorphism card */}
          <div className="relative rounded-2xl border border-white/10 bg-white/5 p-8 shadow-2xl backdrop-blur-xl">
            {/* Language switcher — top right */}
            <div className="absolute right-4 top-4">
              <LanguageSwitcher />
            </div>

            {/* Brand */}
            <div className="mb-8 flex flex-col items-center gap-3 text-center">
              <BrandMark size="lg" />
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-white">
                  {t('app.web')}
                </h1>
                <p className="mt-1 text-sm text-white/50">{t('auth.welcome')}</p>
              </div>
            </div>

            {/* Form */}
            <form className="space-y-4" onSubmit={onSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-white/70 text-xs font-medium">
                  {t('auth.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className="border-white/10 bg-white/5 pl-9 !text-white caret-white placeholder:text-white/30 focus:border-[hsl(188,95%,48%,0.5)] focus:ring-[hsl(188,95%,48%,0.2)]"
                    placeholder="you@eonus.com"
                    {...register('email', { required: true })}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-white/70 text-xs font-medium">
                  {t('auth.password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    className="border-white/10 bg-white/5 pl-9 !text-white caret-white placeholder:text-white/30 focus:border-[hsl(188,95%,48%,0.5)] focus:ring-[hsl(188,95%,48%,0.2)]"
                    placeholder="••••••••"
                    {...register('password', { required: true })}
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="mt-2 w-full bg-[hsl(188,95%,42%)] text-[hsl(224,38%,4%)] font-semibold hover:bg-[hsl(188,95%,50%)] hover:shadow-glow transition-all duration-200"
                disabled={formState.isSubmitting || login.isPending}
              >
                {login.isPending ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {t('common.loading')}
                  </span>
                ) : (
                  t('auth.login')
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
